
export const common = {
  nav: {
    maison: 'Maison',
    collections: 'Collections',
    rituals: 'Rituals',
    journal: 'Journal',
    contact: 'Contact',
    story: 'Story'
  },
  hero: {
    title: 'Giving nature a fragrant voice.',
    subtitle: 'A French botanical perfume house, born between plant science and emotional memory.'
  },
  maison: {
    title: 'Maison',
    mainTitle: 'At SETHWOOD, fragrance is born from memory.\nEach creation seeks to resonate, not to seduce.\nWe do not ask who you are,\nbut what you remember.',
    belief1Title: 'Nature as Origin',
    belief1Subtitle: 'Nature is not an inspiration, it is the starting point',
    belief1Desc: 'Each creation begins with a deep study of plants, their essences and their botanical memory.',
    belief2Title: 'Memory as Essence',
    belief2Subtitle: 'Fragrance is the most honest carrier of memory',
    belief2Desc: 'Our fragrances are designed to awaken memories and reveal intimate emotions.',
    belief3Title: 'Ritual as Lifestyle',
    belief3Subtitle: 'The everyday becomes ritual',
    belief3Desc: 'Each gesture of care is a silent celebration of beauty and consciousness.'
  },
  collections: {
    title: 'Collections',
    subtitle: 'Where nature meets memory',
    handCream: 'Fragrance Hand Cream',
    handCreamDesc: 'A daily gesture, enriched with fine textures and delicate botanical notes. The skin remembers, the fragrance remains.',
    bodyLotion: 'Fragrance Body Lotion',
    bodyLotionDesc: 'A fragrant caress for the body, where nutrition and emotion meet.',
    showerGel: 'Fragrance Shower Gel',
    showerGelDesc: 'A purification ritual inspired by nature, transforming water into a sensory experience.',
    perfume: 'Memory Awakening Perfume',
    perfumeDesc: 'Fragrances designed to awaken buried memories and reveal the intimate emotion of each person.',
    discover: 'Discover',
    explore: 'Explore'
  },
  rituals: {
    title: 'Rituals',
    intro: 'At SETHWOOD, care becomes ritual. Each fragrance accompanies a precise moment of the day — the silent morning, the suspended afternoon, the introspective night.',
    morning: 'The Silent Morning',
    morningDesc: 'Start the day with a clear intention. Light textures and fresh notes gently awaken the senses, preparing the mind for the day ahead.',
    morningProducts: 'Shower Gel · Perfume',
    afternoon: 'The Suspended Afternoon',
    afternoonDesc: 'A moment of pause in the flow of time. Delicate fragrances create a cocoon of serenity, allowing the mind to refocus.',
    afternoonProducts: 'Hand Cream · Perfume',
    night: 'The Introspective Night',
    nightDesc: 'The evening ritual invites contemplation. Deep and enveloping notes accompany the transition to rest, awakening memories and dreams.',
    nightProducts: 'Body Lotion · Perfume'
  },
  journal: {
    title: 'Journal',
    subtitle: 'Stories, reflections and the art of botanical living',
    readMore: 'Read more',
    loadMore: 'Load more articles',
    viewAll: 'View All Articles',
    backToJournal: 'Back to Journal',
    relatedArticles: 'Related Articles',
    allArticles: 'All Articles',
    filterBy: 'Filter by',
    all: 'All',
    showing: 'Showing',
    articles: 'articles',
    noArticles: 'No articles found in this category.',
    category: {
      story: 'Story',
      science: 'Science',
      art: 'Art',
      lifestyle: 'Lifestyle',
      charity: 'Charity'
    },
    articleContent: {
      intro: 'In the quiet laboratories where science meets artistry, a profound transformation occurs. The molecular signatures of botanical specimens are carefully decoded, revealing stories written in the language of terpenes, esters, and aldehydes.',
      section1Title: 'The Science of Memory',
      section1P1: 'What makes botanical fragrances so compelling is their ability to trigger memory and emotion. The olfactory bulb, directly connected to the limbic system, processes scent in a way that bypasses rational thought.',
      section1P2: 'This neurological reality informs every aspect of fragrance design at SETHWOOD. Each composition is crafted not merely to smell pleasant, but to create emotional landscapes.',
      section2Title: 'Artistry in Extraction',
      section2P1: 'The extraction process itself is an art form. Steam distillation, solvent extraction, enfleurage—each method yields different aromatic profiles from the same botanical source.',
      section2P2: 'In our atelier, traditional methods are honored while embracing modern innovations. Supercritical CO2 extraction allows us to capture volatile compounds that would be lost to heat.',
      section3Title: 'The Philosophy of Restraint',
      section3P1: 'True luxury lies not in excess but in refinement. Each SETHWOOD fragrance contains only what is essential—no synthetic fillers, no unnecessary complexity.',
      section3P2: 'The result is a collection of fragrances that feel both timeless and contemporary. They reveal their character gradually, creating an intimate dialogue between wearer and scent.',
      quote: 'A fragrance should be like a whispered secret—intimate, personal, and unforgettable.',
      conclusion: 'This is the essence of SETHWOOD—where generations of botanical research meet the timeless art of perfumery, creating fragrances that honor both the wisdom of nature and the complexity of human emotion.'
    }
  },
  contact: {
    title: 'Contact',
    intro: 'For any collaboration, distribution or creative project, we would be delighted to start a conversation.',
    sendEmail: 'Send Email'
  },
  footer: {
    copyright: '© 2018 - 2026 SETHWOOD. All rights reserved.',
    privacy: 'Privacy',
    terms: 'Terms',
    poweredBy: 'Powered by Sethwood'
  },
  story: {
    heroSubtitle: 'Our Story',
    heroTitle: 'Between Science and Poetry',
    heroDesc: 'The story of SETHWOOD begins in a French botanical garden, where passion for plants meets the art of perfumery.',
    originLabel: 'The Origin',
    originTitle: 'Born from a Lineage of Botanists',
    originP1: 'SETHWOOD finds its roots in a family of scientists dedicated to the study of plants for three generations. In the 1920s, Seth Wood\'s grandfather established the first family botanical laboratory in the south of France, dedicating his life to understanding the medicinal and aromatic properties of Mediterranean plants.',
    originP2: 'This scientific passion is passed down from generation to generation, enriched by a growing artistic sensibility. Seth\'s father, a botanist and poet, began to explore the emotional dimension of plant essences, creating a bridge between scientific rigor and sensory expression.',
    originP3: 'It is in this heritage that SETHWOOD was born in 2018 — a house that honors botanical science while fully embracing the art of perfumery and care. Each creation is the fruit of in-depth research on plants, their essences, and their ability to awaken memory and emotion.',
    philosophyLabel: 'Our Philosophy',
    philosophyTitle: 'The Pillars of Our Vision',
    philosophyDesc: 'Four fundamental principles guide every decision, every creation, every gesture at SETHWOOD. They are the beating heart of our house.',
    philosophy1Title: 'Nature as Language',
    philosophy1Desc: 'We believe that each plant has a unique voice, a story to tell. Our role is to translate this voice into fragrance, texture, sensory experience.',
    philosophy1Detail: 'Each essence is selected for its ability to communicate an emotion, to awaken a memory, to create a deep connection with the wearer.',
    philosophy2Title: 'Time as Ally',
    philosophy2Desc: 'In a world obsessed with speed, we choose slowness. Our creations require time — for maceration, for infusion, for maturation.',
    philosophy2Detail: 'This patience allows the essences to reveal their full complexity, creating fragrances that evolve and transform on the skin.',
    philosophy3Title: 'Emotion as Essence',
    philosophy3Desc: 'Fragrance is not a superficial ornament. It is a carrier of memory, a trigger of emotion, a silent companion of our inner lives.',
    philosophy3Detail: 'Our creations are designed to touch the soul before seducing the senses, to create a deep and lasting emotional resonance.',
    philosophy4Title: 'Ritual as Art of Living',
    philosophy4Desc: 'Each gesture of care becomes a moment of consciousness, a pause in the flow of daily life. We transform routines into rituals.',
    philosophy4Detail: 'Applying a cream, perfuming oneself, taking a shower — these simple acts become celebrations of beauty and presence.',
    quote: 'Fragrance is the most honest language of memory. It never lies, it never betrays. It reveals.',
    quoteAuthor: 'Seth Wood, Founder',
    craftsmanshipLabel: 'Our Craftsmanship',
    craftsmanshipTitle: 'The Art of Botanical Perfumery',
    craftsmanshipDesc: 'Each SETHWOOD creation is the result of meticulous work, where ancestral know-how meets modern innovation.',
    craftsmanship1Title: 'Ingredient Selection',
    craftsmanship1Desc: 'We source our botanical ingredients from the finest terroirs around the world, working directly with farmers who share our commitment to quality and sustainability. Each plant is carefully selected for its aromatic purity and emotional resonance.',
    craftsmanship2Title: 'Artisanal Extraction',
    craftsmanship2Desc: 'Our extraction methods respect the integrity of each plant, using traditional techniques refined over generations to capture the purest essences. We employ steam distillation, cold pressing, and enfleurage to preserve the soul of each botanical.',
    craftsmanship3Title: 'Composition Mastery',
    craftsmanship3Desc: 'Each fragrance is composed with precision and intuition, balancing top, heart and base notes to create harmonious olfactory journeys. Our perfumers work like composers, creating symphonies of scent that evolve beautifully over time.',
    founderLabel: 'The Founder',
    founderTitle: 'Seth Wood',
    founderSubtitle: 'Botanist, Perfumer, Visionary',
    founderP1: 'Born into a family of botanists, Seth Wood grew up surrounded by plants, laboratories, and the intoxicating scents of the French countryside. His childhood was marked by long hours spent in his grandfather\'s greenhouse, learning to identify plants by their leaves, their flowers, and above all, their scent.',
    founderP2: 'After studying botany at the University of Montpellier and perfumery in Grasse, Seth founded SETHWOOD with a clear vision: to create a bridge between the scientific rigor of botanical research and the emotional poetry of perfumery.',
    founderP3: 'Today, Seth continues to lead the creative direction of the house, personally overseeing each new creation and ensuring that every product embodies the values of authenticity, quality, and emotional resonance that define SETHWOOD.',
    heritageLabel: 'Our Heritage',
    heritageTitle: 'A Century of Botanical Passion',
    heritageDesc: 'From 1920 to today, three generations have cultivated a passion for plants and their power to touch the human soul.',
    timeline1Year: '1920',
    timeline1Title: 'The Beginnings',
    timeline1Desc: 'Seth\'s grandfather establishes the first family botanical laboratory in the south of France.',
    timeline2Year: '1965',
    timeline2Title: 'The Evolution',
    timeline2Desc: 'Seth\'s father, a botanist and poet, begins to explore the emotional dimension of plant essences.',
    timeline3Year: '2015',
    timeline3Title: 'The Training',
    timeline3Desc: 'Seth Wood completes his perfumery training in Grasse, the cradle of French perfumery.',
    timeline4Year: '2018',
    timeline4Title: 'The Birth',
    timeline4Desc: 'Founding of SETHWOOD, a house dedicated to the olfactory expression of nature.',
    timeline5Year: '2020',
    timeline5Title: 'The Recognition',
    timeline5Desc: 'First collection awarded at the Fragrance Foundation Awards for its innovative botanical approach.',
    timeline6Year: '2023',
    timeline6Title: 'The Expansion',
    timeline6Desc: 'Opening of the workshop-boutique in Paris and launch of the ritual care line.',
    timeline7Year: '2025',
    timeline7Title: 'Today',
    timeline7Desc: 'SETHWOOD continues to explore the frontier between botanical science and fragrance creation.',
    futureTitle: 'The Future of SETHWOOD',
    futureDesc: 'Our journey continues. We explore new plants, new techniques, new ways to capture the beauty of nature. But our commitment remains unchanged: to create fragrances and care products that honor nature, touch the soul, and transform the everyday into poetic ritual.',
    privacyTitle: 'Privacy Policy',
    privacyLastUpdate: 'Last updated: January 2025',
    privacySection1Title: '1. Information Collection',
    privacySection1Desc: 'SETHWOOD collects personal information when you place an order, create an account or subscribe to our newsletter. This information may include your name, email address, delivery address and payment information.',
    privacySection2Title: '2. Use of Information',
    privacySection2Desc: 'We use your information to process your orders, improve our services, personalize your experience and send you marketing communications (with your consent). Your data will never be sold to third parties.',
    privacySection3Title: '3. Data Protection',
    privacySection3Desc: 'We implement appropriate security measures to protect your personal information against unauthorized access, modification, disclosure or destruction. All transactions are encrypted via SSL.',
    privacySection4Title: '4. Cookies',
    privacySection4Desc: 'Our site uses cookies to improve your browsing experience. You can choose to disable cookies in your browser settings, but this may affect some site features.',
    privacySection5Title: '5. Your Rights',
    privacySection5Desc: 'In accordance with GDPR, you have the right to access, rectify, delete or limit the use of your personal data. To exercise these rights, please contact us at contact@sethwood.fr.',
    privacySection6Title: '6. Contact',
    privacySection6Desc: 'For any questions regarding our privacy policy, please contact us at contact@sethwood.fr.',
    termsTitle: 'Terms of Service',
    termsLastUpdate: 'Last updated: January 2025',
    termsSection1Title: '1. Acceptance of Terms',
    termsSection1Desc: 'By accessing and using the SETHWOOD site, you agree to be bound by these terms of use. If you do not accept these terms, please do not use our site.',
    termsSection2Title: '2. Orders and Payments',
    termsSection2Desc: 'All orders are subject to acceptance and availability. We reserve the right to refuse any order. Prices are indicated in euros and include applicable VAT. Payment must be made at the time of order.',
    termsSection3Title: '3. Delivery',
    termsSection3Desc: 'We strive to deliver your orders within the indicated timeframes. However, we cannot be held responsible for delivery delays due to circumstances beyond our control.',
    termsSection4Title: '4. Returns and Refunds',
    termsSection4Desc: 'You have 14 days from receipt of your order to exercise your right of withdrawal. Products must be returned in their original packaging, unused and unopened. Return costs are your responsibility.',
    termsSection5Title: '5. Intellectual Property',
    termsSection5Desc: 'All content on this site, including texts, images, logos and designs, is the property of SETHWOOD and is protected by intellectual property laws. Any unauthorized reproduction is prohibited.',
    termsSection6Title: '6. Limitation of Liability',
    termsSection6Desc: 'SETHWOOD cannot be held responsible for direct or indirect damages resulting from the use of our products or our website, within the limits authorized by law.',
    termsSection7Title: '7. Modifications',
    termsSection7Desc: 'We reserve the right to modify these terms of use at any time. Modifications will take effect upon publication on the site.',
    termsSection8Title: '8. Contact',
    termsSection8Desc: 'For any questions regarding our terms of use, please contact us at contact@sethwood.fr.'
  },
  cookie: {
    title: 'We Value Your Privacy',
    description: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies in accordance with our Cookie Policy.',
    learnMore: 'For more information, please read our',
    privacyPolicy: 'Privacy Policy',
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    customize: 'Customize',
    preferencesTitle: 'Cookie Preferences',
    necessary: 'Necessary Cookies',
    necessaryDesc: 'These cookies are essential for the website to function properly. They enable basic functions like page navigation and access to secure areas.',
    alwaysActive: 'Always Active',
    analytics: 'Analytics Cookies',
    analyticsDesc: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
    marketing: 'Marketing Cookies',
    marketingDesc: 'These cookies are used to track visitors across websites to display relevant advertisements based on your interests.',
    savePreferences: 'Save Preferences'
  }
};
